import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from "chart.js";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { ArrowUprightCircleIcon } from "../../../../../assets/icons";
import { CardFrame } from "../../../../../common/components/card-frame/CardFrame";
import { MainValue } from "../../../../../common/components/main-value/MainValue";
import { useGetMeterConsumptionGraphQuery } from "../../../../../service/vending-service";
import { useChartTokens } from "../../../../../theme/chart-tokens";
import { formatKwh } from "../../utils";
import { ConsumptionChartCardSkeleton } from "./ConsumptionChartCardSkeleton";
import styles from "./consumption-chart-card.module.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DEFAULT_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CHART_TOKENS = [
	"bannerError",
	"primaryText01",
	"primaryText09",
	"baseWhite",
	"primaryText05",
] as const;

function getWeekRange() {
	const now = new Date();
	const dayOfWeek = now.getDay();
	const monday = new Date(now);
	monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
	monday.setHours(0, 0, 0, 0);
	const sunday = new Date(monday);
	sunday.setDate(monday.getDate() + 6);
	sunday.setHours(23, 59, 59, 999);
	return { from: monday.toISOString(), to: sunday.toISOString() };
}

export const ConsumptionChartCard = () => {
	const navigate = useNavigate();
	const weekRange = useMemo(() => getWeekRange(), []);

	const { data, isFetching } = useGetMeterConsumptionGraphQuery({
		from: weekRange.from,
		to: weekRange.to,
		isMonthly: false,
	});

	const chartColors = useChartTokens(CHART_TOKENS);

	const chartItems = useMemo(() => {
		if (!data?.data?.length) return null;
		return [...data.data].reverse();
	}, [data]);

	const hasData = !isFetching && chartItems != null;

	const labels = hasData ? chartItems!.map(item => DAY_ABBR[new Date(item.date).getDay()]) : DEFAULT_LABELS;
	const values = hasData ? chartItems!.map(item => item.consumption) : Array(7).fill(0);
	const totalConsumption = hasData ? values.reduce((sum: number, v: number) => sum + v, 0) : 0;

	const chartData = {
		labels,
		datasets: [
			{
				data: values,
				backgroundColor: hasData ? chartColors.bannerError : chartColors.primaryText01,
				borderRadius: 2,
				barPercentage: 0.95,
				categoryPercentage: 0.95,
			},
		],
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: { label: (ctx: any) => `${ctx.parsed.y} kWh` },
				backgroundColor: chartColors.primaryText09,
				bodyColor: chartColors.baseWhite,
				padding: 8,
			},
		},
		scales: {
			x: {
				grid: { display: false },
				ticks: { color: chartColors.primaryText05, font: { size: 12 } },
				border: { display: false },
			},
			y: { display: false },
		},
	};

	if (isFetching) return <ConsumptionChartCardSkeleton />;

	return (
		<div className={styles.wrapper}>
		<CardFrame>
			<div className={styles.inner}>
				<div className={styles.header}>
					<div className={styles.headerLeft}>
						<span className={styles.title}>This Week&apos;s Consumption Trend</span>
						<MainValue value={formatKwh(totalConsumption)} unit="kWh" />
					</div>
					<button className={styles.analyticsLink} onClick={() => navigate("/energy-management/analytics")}>
						<ArrowUprightCircleIcon width={22} height={22} />
						Full Analytics
					</button>
				</div>
				<div className={styles.chartWrapper}>
					<Bar data={chartData} options={chartOptions as any} />
				</div>
			</div>
		</CardFrame>
		</div>
	);
};
